import type { MapThemePref, VehicleDisplay } from '../../store/settings'
import { LATE_NIGHT_TO_HOUR } from './mapTheme'
import { LIVERIES, LIVERY_IDS_FOR, LIVERY_VEHICLES, type LiveryVehicle } from './liveries'

/**
 * The prose pickers, and the only settings on this sheet that become LEVELS: four described
 * modes each, which cost more height inline than everything else on the sheet put together.
 */
export type LayersLevelId = 'display' | 'theme'

/**
 * Every exclusive setting the sheet renders as a RadioGroup. Being a CHOICE and being a
 * LEVEL are separate facts: the two livery grids are exclusive too and stay inline on the
 * root level, because a grid of vehicle renders reads at a glance where prose has to be
 * read — see the drill-down note in `LayersSheet.svelte`.
 */
export type LayersChoiceId = LayersLevelId | LiveryVehicle

export const DISPLAY_OPTIONS: Array<{
  value: VehicleDisplay
  label: string
  description: string
}> = [
  { value: 'labels', label: 'Labels', description: 'Only route bullets at all zooms (lightest battery impact)' },
  { value: 'trains', label: '3D models', description: 'Every vehicle as a model, even when zoomed out' },
  { value: 'both', label: 'Both', description: '3D models with route bullets floating above' },
  { value: 'optimized', label: 'Optimized', description: '3D models up close, route bullets when zoomed out' },
]

export const THEME_OPTIONS: Array<{
  value: MapThemePref
  label: string
  description: string
}> = [
  { value: 'device', label: 'Device', description: 'Follow your phone’s light or dark setting' },
  {
    value: 'schedule',
    label: 'Service',
    description: `Light by day, dark through late-night service (midnight-${LATE_NIGHT_TO_HOUR} AM)`,
  },
  { value: 'light', label: 'Light', description: 'Always the light basemap' },
  { value: 'dark', label: 'Dark', description: 'Always the dark basemap' },
]

export const LIVERY_PICKERS = Object.fromEntries(
  LIVERY_VEHICLES.map((vehicle) => [
    vehicle,
    {
      vehicle,
      options: LIVERY_IDS_FOR[vehicle].map((id) => ({
        value: id,
        label: LIVERIES[id].label,
        description: LIVERIES[id].note,
      })),
    },
  ]),
) as Record<LiveryVehicle, {
  vehicle: LiveryVehicle
  options: Array<{ value: string; label: string; description: string }>
}>
